<script lang="ts" setup>
import axios from "axios";
import axiosdb from "@/axios-db";
import axiosaux from "@/axios-aux";
import { jsonToGraphQLQuery, EnumType } from "json-to-graphql-query";
import { onMounted, ref } from "vue";
import LoadingModal from "@/shared/LoadingModal.vue";
import ErrorModal from "@/shared/ErrorModal.vue";
import { useToast } from "vue-toast-notification";
import { useRoute } from "vue-router";
import { BButton, BCol, BRow } from "bootstrap-vue-next";
// Instance Variables
const toast = useToast();
const route = useRoute();
// Component Variables
const apiURL = ref(axiosaux.defaults.baseURL);
const dataLoaded = ref<boolean>(false);
const specificError = ref("");
const forms = ref<any[]>([]);
const ipInfo = ref({});
const userAgent = ref("");
const confirmed = ref(false);
const cipher = ref();
// Modals
const loadingVisible = ref<boolean>(false);
const errorVisible = ref<boolean>(false);

function toGQL(queryObject, variables = {}) {
  // Using the jsonToGraphQuery library to convert the json defined query into a graphQL query
  return { query: jsonToGraphQLQuery(queryObject), variables: variables };
}

async function loadData() {
  loadingVisible.value = true;

  let query = {
    query: {
      halo_form_find_forms: {
        __args: {
          cipher: cipher.value,
        },
        results: true,
      },
    },
  };

  let res = await axiosdb.post(
    "/graphql?t=" + new Date().valueOf(),
    toGQL(query)
  );

  if (res.data.errors) {
    specificError.value = res.data.errors[0].message;
    loadingVisible.value = false;

    errorVisible.value = true;
  } else {
    dataLoaded.value = true;
    forms.value = res.data.data.halo_form_find_forms.results;
  }
  loadingVisible.value = false;
}

async function sign() {
  let emails: any[] = [];
  // Ensure that all forms have been signed
  for (let form of forms.value) {
    if (!(form.agreeSignature || form.disagreeSignature)) {
      toast.error("Please read and sign the '" + form.form.title + "' form");
      return false;
    }
    if (form.agreeSignature && form.disagreeSignature) {
      toast.error(
        "Please fill in either the agreement or disagreement box, not both, for the '" +
          form.form.title +
          "' form"
      );
      return false;
    }
    // Collect each signature so we can make sure they are the same
    form.agreeSignature ? emails.push(form.agreeSignature) : null;
    form.disagreeSignature ? emails.push(form.disagreeSignature) : null;
  }
  // If they are not the same the we have a problem
  if (!emails.every((val, i, arr) => val === arr[0])) {
    toast.error("All email addresses must be the same");
    return false;
  }

  let signatures = forms.value.map((a) => {
    let agreedTo = a.agreeSignature ? true : false;
    let email = a.agreeSignature ? a.agreeSignature : a.disagreeSignature;
    return {
      id: a.id,
      cipher: cipher.value,
      agreed_to: agreedTo,
      email: email,
      ip_info: ipInfo.value,
      user_agent: userAgent.value,
    };
  });

  loadingVisible.value = true;

  let mutation = {
    mutation: {
      halo_form_sign: {
        __args: {
          signatures: signatures,
        },
        student_id: true,
      },
    },
  };

  let res = await axiosdb.post(
    "/graphql?t=" + new Date().valueOf(),
    toGQL(mutation)
  );

  if (res.data.errors) {
    toast.error(res.data.errors[0].message);
  } else {
    confirmed.value = true;
  }
  loadingVisible.value = false;
}

function onMount() {
  cipher.value = route.params.cipher;
  loadData();
  userAgent.value = navigator.userAgent;
  axios
    .get(
      "https://api.ipdata.co/?api-key=f4b7457e529350738be6ec6e88c917471da225b4921b92e222b1197d"
    )
    .then((response) => {
      ipInfo.value = response.data;
    });
}

onMounted(onMount);
</script>
<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="card border-top-2 border-top-info col-md-8 offset-md-2">
        <div class="card-body">
          <div class="text-center">
            <img
              class="navbar-brand-full"
              src="img/logo.png"
              height="100"
              alt="LO Logo"
            />
            <h2>Learning Online Registration</h2>
          </div>

          <ul class="step d-flex flex-nowrap pt-4">
            <li class="step-item">
              <span>Step 1<br />Student registers</span>
            </li>
            <li class="step-item active">
              <span
                >Step 2<br />Student & parent/guardian agree to appropriate
                policy documents</span
              >
            </li>
            <li class="step-item">
              <span
                >Step 3<br />Student enrollments will be reviewed and
                approved</span
              >
            </li>
            <li class="step-item">
              <span>Step 4<br />Student will be enrolled into classes</span>
            </li>
          </ul>

          <BRow>
            <BCol v-if="dataLoaded && !confirmed && forms.length > 0">
              <h2 class="mt-4">Forms</h2>
              <p class="mt-3 mb-4">
                There are {{ forms.length }} forms(s) that require your
                attention. All of these must be signed before the student can
                begin their course(s). The form is valid for the school year
                specified. If you have any questions or concerns regarding the
                statements or documents please contact
                <a href="mailto:learningonline@rcsd.ca"
                  >learningonline@rcsd.ca</a
                >. Information about your web browser and IP address are
                collected by this page.
              </p>

              <div v-for="(form, key) of forms" :key="key" class="mb-5">
                <h4>
                  {{ form.form.title }} - {{ form.school_year.name }} -
                  {{ form.student.first_name }} {{ form.student.last_name }}
                </h4>
                <div v-if="form.form.file">
                  <!-- #toolbar = 0 prevents the document from being auto-downloaded if the browser does not support it -->
                  <object
                    class="w-100"
                    :height="500"
                    :data="apiURL + form.form.file.path"
                    type="application/pdf"
                  >
                    <iframe
                      class="w-100"
                      :height="500"
                      :src="apiURL + form.form.file.path + '#toolbar=0'"
                    >
                      <p>Your browser does not support PDFs</p>
                    </iframe>
                  </object>
                  <p class="text-right">
                    <a :href="apiURL + form.form.file.path">Download a copy</a>
                  </p>
                </div>
                <p>{{ form.form.body }}</p>

                <div class="row">
                  <div class="form-group col">
                    <label
                      ><b
                        >To indicate your
                        <u class="text-success">agreement</u> to the above
                        please enter your email address below. Use the same
                        email address where you received the link for this page.
                        <span v-if="form.form.agreement_required"
                          >Your agreement to the above is required for
                          enrollment into this course.</span
                        ></b
                      ></label
                    >
                  </div>
                  <div
                    class="form-group col"
                    v-if="!form.form.agreement_required"
                  >
                    <label
                      ><b
                        >To indicate your
                        <u class="text-danger">disagreement</u> to the above
                        please enter your email address below. Use the same
                        email address where you received the link for this
                        page.</b
                      ></label
                    >
                  </div>
                </div>
                <div class="row">
                  <div class="form-group col">
                    <input
                      type="text"
                      class="form-control text-center border"
                      v-model="form.agreeSignature"
                    />
                    <div class="invalid-tooltip">This field is required</div>
                  </div>
                  <div
                    class="form-group col"
                    v-if="!form.form.agreement_required"
                  >
                    <input
                      type="text"
                      class="form-control text-center border"
                      v-model="form.disagreeSignature"
                    />
                    <div class="invalid-tooltip">This field is required</div>
                  </div>
                </div>
              </div>
              <div class="d-grid gap-2">
                <BButton
                  class="btn-block text-white"
                  variant="success"
                  @click="sign"
                  >Submit</BButton
                >
              </div>
            </BCol>

            <BCol v-if="dataLoaded && !confirmed && forms.length == 0">
              <h5 class="mt-5 text-center">
                There are no forms that require your signature.
              </h5>
            </BCol>

            <BCol v-if="confirmed">
              <h4 class="mt-5 text-center">
                Thank you! Your signature on these forms has been recorded.
              </h4>
            </BCol>
          </BRow>
        </div>
      </div>
    </div>

    <LoadingModal :visible="loadingVisible"></LoadingModal>

    <ErrorModal :visible="errorVisible"></ErrorModal>
    
  </div>
</template>
